import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'

import { API_BASE_URL, API_TIMEOUT_MS } from '@/constants'
import type { ApiResponse } from '@/types/api'
import { ApiError } from '@/types/api'

import { getToken, refreshTokenFromBridge } from './tokenStore'

const TOKEN_EXPIRED_STATUS = 401

/** 401 재시도 1회 마킹 — 재발급 후에도 401이면 그대로 에러 전파(무한 루프 방지) */
interface RequestWithRetry extends AxiosRequestConfig {
  _tokenRetried?: boolean
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
})

export interface HttpClient extends AxiosInstance {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  patch<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
}

const api: HttpClient = axiosInstance

api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  async (response) => {
    const body = response.data as ApiResponse<unknown>
    const reqConfig = response.config as RequestWithRetry
    // 401 (토큰 만료) → 브릿지 getAuthToken 으로 재발급 후 원요청 1회 재시도
    if (body.status === TOKEN_EXPIRED_STATUS && !reqConfig._tokenRetried) {
      reqConfig._tokenRetried = true
      const refreshed = await refreshTokenFromBridge()
      if (refreshed) return api.request(reqConfig)
    }
    if (body.error) {
      throw new ApiError(body.error.code, body.error.description, body.status, body.txid)
    }
    return body.data as never
  },
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as ApiResponse<unknown> | undefined
      const reqConfig = error.config as RequestWithRetry | undefined
      // HTTP 401 → 동일하게 재발급 + 1회 재시도. 갱신 실패 시 아래에서 원래 에러 전파
      const is401 = error.response.status === TOKEN_EXPIRED_STATUS || body?.status === TOKEN_EXPIRED_STATUS
      if (is401 && reqConfig && !reqConfig._tokenRetried) {
        reqConfig._tokenRetried = true
        const refreshed = await refreshTokenFromBridge()
        if (refreshed) return api.request(reqConfig)
      }
      if (body?.error) {
        return Promise.reject(
          new ApiError(body.error.code, body.error.description, body.status ?? error.response.status, body.txid)
        )
      }
    }
    return Promise.reject(error)
  }
)

export default api
